import invariant from 'react-utils/invariant';
import PortalIdParser from 'PortalIdParser';
import { Map as ImmutableMap, Set as ImmutableSet, List } from 'immutable';

import { fetchAvatars, normalizeUpdates } from '../api/AvatarAPI';
import defaultFetchAvatars from '../api/defaultFetchAvatars';
import { getAuthConfig } from '../api/avatarRpcClient';

import LookupRecord from '../Records/LookupRecord';

import { debounce, once } from './utils';
import * as Atom from './Atom';

import {
  AvatarCache,
  AvatarCacheAtom,
  AvatarCacheEntry,
  PendingQueue,
  PendingQueueAtom,
} from '../types/CacheEntries';
import {
  DEFAULT_CACHE_ENTRY,
  FIRST_TRY_DELAY,
  LAST_TRY_DELAY,
  SECOND_TRY_DELAY,
} from '../constants/cacheConstants';

/**
 * Given the various state Atoms, retrieves a URI from the cache or adds the
 * lookup to the queue.
 */
export function getAvatarCacheEntry(
  cacheAtom: AvatarCacheAtom,
  pendingAtom: PendingQueueAtom,
  enqueue: (
    fetcher: (...args: any[]) => Promise<any>,
    portalId: number | null | undefined,
    lookups: PendingQueue,
    trellisDefault?: boolean
  ) => void,
  fetcher: (...args: any[]) => Promise<any>,
  portalId: number | null | undefined,
  lookup: LookupRecord,
  trellisDefault?: boolean
) {
  invariant(
    typeof fetcher === 'function',
    'expected `fetcher` to be a function but got `%s`',
    fetcher
  );
  invariant(
    typeof portalId === 'number' || !lookup.requiresPortalId(),
    'expected `portalId` to be a number or the lookup to not require a portalId but got `%s`',
    portalId
  );
  invariant(
    lookup instanceof LookupRecord,
    'expected `lookup` to be a LookupRecord but got `%s`',
    lookup
  );

  const avatars = Atom.deref(cacheAtom);
  if (avatars.has(lookup)) {
    return avatars.get(lookup) || DEFAULT_CACHE_ENTRY;
  }

  if (!Atom.deref(pendingAtom).has(lookup)) {
    Atom.swap(pendingAtom, (current) => current.union(ImmutableSet.of(lookup)));
    enqueue(fetcher, portalId, ImmutableSet.of(lookup), trellisDefault);
  }

  return DEFAULT_CACHE_ENTRY;
}

export function makeCacheAtom(
  initialCache = ImmutableMap<LookupRecord, AvatarCacheEntry>()
): AvatarCacheAtom {
  return Atom.atom(initialCache, ImmutableMap.isMap);
}

export function makeQueueAtom(
  initialQueue = ImmutableSet<LookupRecord>()
): PendingQueueAtom {
  return Atom.atom(initialQueue, ImmutableSet.isSet);
}

/**
 * Adds lookups to the queue and schedules a queue flush.
 */
export function queuer(
  queueAtom: PendingQueueAtom,
  flushQueue: (
    fetcher: (...args: any[]) => Promise<any>,
    portalId: number | null | undefined,
    trellisDefault?: boolean
  ) => void,
  fetcher: (...args: any[]) => Promise<any>,
  portalId: number | null | undefined,
  lookups: PendingQueue | null,
  trellisDefault?: boolean
) {
  invariant(
    !lookups || (ImmutableSet.isSet(lookups) && !lookups.isEmpty()),
    'expected `lookups` to be a non-empty Set but got `%s`',
    lookups
  );

  Atom.swap(queueAtom, (current) => current.union(lookups!));
  flushQueue(fetcher, portalId, trellisDefault);
}

/**
 * Creates a queing function bound to a queue Atom.
 */
export function makeQueuer(
  queueAtom: PendingQueueAtom,
  doEnqueue: typeof queuer,
  doFlush: typeof flush,
  cacheAtom: AvatarCacheAtom,
  pendingAtom: PendingQueueAtom,
  nextEnqueue?: (
    fetcher: (...args: any[]) => Promise<any>,
    portalId: number | null | undefined,
    retry: PendingQueue,
    trellisDefault?: boolean
  ) => void
) {
  const flushQueue = (
    fetcher: (...args: any[]) => Promise<any>,
    portalId: number | null | undefined,
    trellisDefault?: boolean
  ) =>
    doFlush(
      cacheAtom,
      queueAtom,
      pendingAtom,
      nextEnqueue,
      fetcher,
      portalId,
      trellisDefault
    );

  return (
    fetcher: (...args: any[]) => Promise<any>,
    portalId: number | null | undefined,
    lookups: PendingQueue,
    trellisDefault?: boolean
  ) =>
    doEnqueue(
      queueAtom,
      flushQueue,
      fetcher,
      portalId,
      lookups,
      trellisDefault
    );
}

/**
 * Splits lookups into buckets based on their dimensions so the fetcher can make
 * separate calls for each dimension.
 */
export function toDimensionBuckets(lookups: PendingQueue) {
  return lookups.reduce((dimensionBuckets, lookup) => {
    return dimensionBuckets.update(lookup.dimensions, (maybeSet) => {
      return ImmutableSet.isSet(maybeSet)
        ? (maybeSet as ImmutableSet<LookupRecord>).add(lookup)
        : ImmutableSet.of(lookup);
    });
  }, ImmutableMap<number | undefined, PendingQueue>());
}

/**
 *  Splits the lookup set into batches in order so the fetcher can make
 *  batch requests to Avatars
 */
export function toBatches(lookupSet: PendingQueue, batchSize = 200) {
  invariant(
    typeof batchSize === 'number' && batchSize > 0,
    'expected `batchSize` to number greater than 0 but got `%s`',
    batchSize
  );
  return lookupSet.reduce((batches, lookup) => {
    if (batches.last().size >= batchSize) {
      return batches.push(ImmutableSet.of(lookup));
    }
    return batches.set(-1, batches.last().add(lookup));
  }, List.of(ImmutableSet<LookupRecord>()));
}

/**
 * Try to resolve the contents of a queue, adding hits to the cache and pushing
 * retries to the next queue.
 */
export function flush(
  cacheAtom: AvatarCacheAtom,
  queueAtom: PendingQueueAtom,
  pendingAtom: PendingQueueAtom,
  nextFlush:
    | ((
        fetcher: (...args: any[]) => Promise<any>,
        portalId: number | null | undefined,
        retry: PendingQueue,
        trellisDefault?: boolean
      ) => void)
    | undefined,
  fetcher: (...args: any[]) => Promise<any>,
  portalId: number | null | undefined,
  trellisDefault?: boolean
) {
  const lookups = Atom.deref(queueAtom);
  if (lookups.isEmpty()) {
    return;
  }
  Atom.swap(queueAtom, (current) => current.clear());

  lookups
    .groupBy((lookup) => lookup.authKey)
    .forEach((authLookups, authKey) => {
      const authGroup = ImmutableSet<LookupRecord>(authLookups);
      const batchAuthConfig = getAuthConfig(authKey);
      toDimensionBuckets(authGroup).forEach((lookupSet, dimensions) => {
        toBatches(lookupSet).forEach((batch) => {
          fetchAvatars(
            fetcher,
            portalId || undefined,
            batch,
            dimensions,
            trellisDefault,
            batchAuthConfig
          )
            .then(({ retry, updates }) => {
              // Add the fetched avatars to the cache
              Atom.swap(cacheAtom, (current) => current.merge(updates));
              // Remove only the lookups that were successfully fetched from the pending queue
              Atom.swap(pendingAtom, (current) =>
                current.subtract(updates.keySeq().toSet())
              );

              if (nextFlush && !retry.isEmpty()) {
                nextFlush(fetcher, portalId, retry, trellisDefault);
              } else {
                // Add the lookups that failed after retries to the cache as DEFAULT_CACHE_ENTRY's
                Atom.swap(cacheAtom, (current) =>
                  current.merge(normalizeUpdates(retry, {}))
                );
                // Remove lookups that failed after retries from the pending queue
                Atom.swap(pendingAtom, (current) => current.subtract(retry));
              }
            })
            .catch((err) => {
              setTimeout(() => {
                throw err;
              });
            });
        });
      });
    });
}

export interface AvatarResolver {
  avatars: AvatarCacheAtom;
  getAvatarCacheEntry(
    lookup: LookupRecord,
    fetcher?: (...args: any[]) => Promise<any>,
    portalId?: number,
    trellisDefault?: boolean
  ): AvatarCacheEntry;
  setAvatarURI(lookup: LookupRecord, value: string | null): void;
  watch: (callback: Atom.Watcher<AvatarCache>) => Atom.AtomType<AvatarCache>;
  unwatch: (callback: Atom.Watcher<AvatarCache>) => Atom.AtomType<AvatarCache>;
}

/**
 * Factory that creates the various state Atoms and bound functions that compose
 * an AvatarResolver and cache.
 *
 * @return {Object}
 */
export function makeAvatarResolver(): AvatarResolver {
  const cacheAtom = makeCacheAtom();
  const pendingAtom = makeQueueAtom();
  const firstQueueAtom = makeQueueAtom();
  const secondQueueAtom = makeQueueAtom();
  const lastQueueAtom = makeQueueAtom();

  const enqueueLastTry = makeQueuer(
    lastQueueAtom,
    queuer,
    debounce(flush, LAST_TRY_DELAY),
    cacheAtom,
    pendingAtom
  );

  const enqueueSecondTry = makeQueuer(
    secondQueueAtom,
    queuer,
    debounce(flush, SECOND_TRY_DELAY),
    cacheAtom,
    pendingAtom,
    enqueueLastTry
  );

  const enqueueFirstTry = makeQueuer(
    firstQueueAtom,
    queuer,
    debounce(flush, FIRST_TRY_DELAY),
    cacheAtom,
    pendingAtom,
    enqueueSecondTry
  );

  return {
    /**
     * The Atom that stores the lookup => url cache. It's usually best to use
     * the helper methods (getAvatarCacheEntry, setAvatarURI, etc.) rather than using
     * the cache directly.
     *
     * @prop  {Atom<Map<LookupRecord,?string>>}
     */
    avatars: cacheAtom,

    /**
     * Returns an avatar uri from the cache or queues the lookup to be fetched.
     */
    getAvatarCacheEntry(
      lookup: LookupRecord,
      fetcher = defaultFetchAvatars,
      portalId = PortalIdParser.get() || 0,
      trellisDefault?: boolean
    ) {
      return getAvatarCacheEntry(
        cacheAtom,
        pendingAtom,
        enqueueFirstTry,
        fetcher,
        portalId,
        lookup,
        trellisDefault
      );
    },

    /**
     * Associate an avatar uri with a lookup in the cache. This is useful when
     * client interaction changes the image an avatar should display (i.e. the
     * user uploaded a new profile photo for a contact)
     */
    setAvatarURI(lookup: LookupRecord, value: string | null) {
      const key = lookup;
      invariant(
        key instanceof LookupRecord,
        'expected `lookup` to be an instance of LookupRecord but got `%s`',
        lookup
      );
      invariant(
        value === null || (typeof value === 'string' && value.length > 0),
        'expected `value` to be a non-empty string or `null` but got `%s`',
        value
      );

      Atom.swap(cacheAtom, (current): AvatarCache => {
        return current
          .set(lookup, {
            uri: value,
            source: null, // no longer a default if value is set & if not src will be null.
          })
          .map((cachedValue, cachedLookup) => {
            return lookup.isSameAs(cachedLookup)
              ? { uri: value, source: null }
              : cachedValue;
          }) as AvatarCache;
      });
    },

    /**
     * Register a callback to be triggered whenever the avatars caches changes.
     *
     * @param  {Function}  callback
     * @return void
     */
    watch: (callback: Atom.Watcher<AvatarCache>) =>
      Atom.watch(cacheAtom, callback),

    /**
     * Unregister a callback that was previously registered with `watch`.
     *
     * @param  {Function}  callback
     * @return void
     */
    unwatch: (callback: Atom.Watcher<AvatarCache>) =>
      Atom.unwatch(cacheAtom, callback),
  };
}

/**
 * Returns the AvatarResolver singleton used by Avatar.
 *
 * @return {Object}
 */
export const getAvatarResolver = once(makeAvatarResolver);
